import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        defaultXl:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90 text-xl",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        blue: "border-brand600 bg-blue-200 text-blue-800 w-fit self-center rounded-xl border px-4 py-1",
        yellow:
          "border border-yellow-500 bg-yellow-100/80 text-yellow-800 w-fit self-center rounded-xl border px-4 py-1",
        green:
          "border border-green-500 bg-green-100 text-green-700 w-fit self-center rounded-xl border px-4 py-1",
        orange:
          "border border-yellow-800 bg-orange-100/70 text-orange-500 w-fit self-center rounded-xl border px-4 py-1",
        brown:
          "border border-yellow-800 bg-orange-700/30 text-orange-900 w-fit self-center rounded-xl border px-4 py-1",
        gray: "border border-gray-500 bg-gray-200 text-gray-600 w-fit self-center rounded-xl border px-4 py-1",
        white:
          "border border-gray-500 bg-white text-gray-700 w-fit self-center rounded-xl border px-4 py-1",
        purple:
          "border text-purple-700 border-purple-500 bg-purple-100 w-fit self-center rounded-xl border px-4 py-1",
        indigo:
          "border text-indigo-700 border-indigo-500 bg-indigo-50 w-fit self-center rounded-xl border px-4 py-1",
        lightGray:
          "border text-gray-700 border-gray-200 bg-gray-50 w-fit self-center rounded-xl border px-4 py-1",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
